import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
} from "@chakra-ui/react";
import { DEFAULT_TOAST_DURATION } from "@kg-pos/common";
import {
  CreateNaikinKyuyoInput,
  NaikinKyuyoDataFragment,
  UpdateNaikinKyuyoInput,
  useCreateNaikinKyuyoMutation,
  useUpdateNaikinKyuyoMutation,
} from "__generated__/graphql";
import { MonthYearPicker } from "components/atoms/month-year-picker";
import { RequireField } from "components/atoms/require-field";
import dayjs from "dayjs";
import { Controller, useForm } from "react-hook-form";

export const NaikinKyuyoModal: React.VFC<{
  isOpen: boolean;
  onClose: () => void;
  hostId: string;
  data?: NaikinKyuyoDataFragment | null;
  refetch: any;
}> = ({ isOpen, onClose, hostId, data, refetch }) => {
  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    control,
  } = useForm({
    defaultValues: {
      koteikyu: data?.koteikyu ?? 0,
      uriage1: data?.uriage1 ?? 0,
      uriage2: data?.uriage2 ?? 0,
      shimei1: data?.shimei1 ?? 0,
      shimei2: data?.shimei2 ?? 0,
      startMonth: new Date(data?.startMonth ?? dayjs().startOf("month").toDate()),
      endMonth: data?.endMonth ? new Date(data.endMonth) : null,
      hostId: data?.hostId ?? hostId,
    },
  });
  const [createNaikinKyuyo] = useCreateNaikinKyuyoMutation();
  const [updateNaikinKyuyo] = useUpdateNaikinKyuyoMutation();
  const toast = useToast();

  const isEdit = !!data;

  const onSubmit = async (
    values: { id: string } & (CreateNaikinKyuyoInput | UpdateNaikinKyuyoInput)
  ): Promise<void> => {
    // ミリ秒がずれててソートに影響する可能性があるので0に揃える
    values.startMonth = dayjs(values.startMonth).startOf("day").toDate();
    if (values.endMonth) {
      values.endMonth = dayjs(values.endMonth).startOf("day").toDate();
    }
    if (isEdit && data) {
      values.id = data?.id;
      await updateNaikinKyuyo({
        variables: {
          ...values,
        },
      });
    } else {
      await createNaikinKyuyo({
        variables: {
          ...values,
        },
      });
    }

    toast({
      title: `内勤給与を${isEdit ? "編集" : "登録"}しました。`,
      status: "success",
      duration: DEFAULT_TOAST_DURATION,
      isClosable: true,
    });
    await refetch();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader>内勤給与{isEdit ? "編集" : "入力"}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl id="koteikyu" isInvalid={!!errors.koteikyu}>
              <FormLabel>
                固定給
                <RequireField />
              </FormLabel>
              <Input
                type="number"
                placeholder="固定給を入力してください。"
                background="grey.300"
                {...register("koteikyu", {
                  valueAsNumber: true,
                  required: "固定給が必要です。",
                  min: {
                    value: 0,
                    message: "0円以上で入力してください。",
                  },
                  max: {
                    value: 1_000_000_000,
                    message: "1,000,000,000円以下で入力してください。",
                  },
                })}
                // マウスホイールによる数値編集の無効化
                // see: https://qiita.com/kinakomoti-t/items/ce4402419f2018aae7cf
                onFocus={(e) =>
                  e.target.addEventListener(
                    "wheel",
                    (e) => {
                      e.preventDefault();
                    },
                    { passive: false }
                  )
                }
              />
              <FormErrorMessage>{errors.koteikyu?.message}</FormErrorMessage>
            </FormControl>
            <FormControl id="uriage1" mt={3} isInvalid={!!errors.uriage1}>
              <FormLabel>
                役職手当 売上1
                <RequireField />
              </FormLabel>
              <Input
                type="number"
                placeholder="役職手当 売上1を入力してください。"
                background="grey.300"
                {...register("uriage1", {
                  valueAsNumber: true,
                  required: "役職手当 売上1が必要です。",
                  min: {
                    value: 0,
                    message: "0円以上で入力してください。",
                  },
                  max: {
                    value: 1_000_000_000,
                    message: "1,000,000,000円以下で入力してください。",
                  },
                })}
                // マウスホイールによる数値編集の無効化
                // see: https://qiita.com/kinakomoti-t/items/ce4402419f2018aae7cf
                onFocus={(e) =>
                  e.target.addEventListener(
                    "wheel",
                    (e) => {
                      e.preventDefault();
                    },
                    { passive: false }
                  )
                }
              />
              <FormErrorMessage>{errors.uriage1?.message}</FormErrorMessage>
            </FormControl>
            <FormControl id="uriage2" mt={3} isInvalid={!!errors.uriage2}>
              <FormLabel>
                役職手当 売上2
                <RequireField />
              </FormLabel>
              <Input
                type="number"
                placeholder="役職手当 売上2を入力してください。"
                background="grey.300"
                {...register("uriage2", {
                  valueAsNumber: true,
                  required: "役職手当 売上2が必要です。",
                  min: {
                    value: 0,
                    message: "0円以上で入力してください。",
                  },
                  max: {
                    value: 1_000_000_000,
                    message: "1,000,000,000円以下で入力してください。",
                  },
                })}
                // マウスホイールによる数値編集の無効化
                // see: https://qiita.com/kinakomoti-t/items/ce4402419f2018aae7cf
                onFocus={(e) =>
                  e.target.addEventListener(
                    "wheel",
                    (e) => {
                      e.preventDefault();
                    },
                    { passive: false }
                  )
                }
              />
              <FormErrorMessage>{errors.uriage2?.message}</FormErrorMessage>
            </FormControl>
            <FormControl id="shimei1" mt={3} isInvalid={!!errors.shimei1}>
              <FormLabel>
                役職手当 指名1
                <RequireField />
              </FormLabel>
              <Input
                type="number"
                placeholder="役職手当 指名1を入力してください。"
                background="grey.300"
                {...register("shimei1", {
                  valueAsNumber: true,
                  required: "役職手当 指名1が必要です。",
                  min: {
                    value: 0,
                    message: "0円以上で入力してください。",
                  },
                  max: {
                    value: 1_000_000_000,
                    message: "1,000,000,000円以下で入力してください。",
                  },
                })}
                // マウスホイールによる数値編集の無効化
                // see: https://qiita.com/kinakomoti-t/items/ce4402419f2018aae7cf
                onFocus={(e) =>
                  e.target.addEventListener(
                    "wheel",
                    (e) => {
                      e.preventDefault();
                    },
                    { passive: false }
                  )
                }
              />
              <FormErrorMessage>{errors.shimei1?.message}</FormErrorMessage>
            </FormControl>
            <FormControl id="shimei2" mt={3} isInvalid={!!errors.shimei2}>
              <FormLabel>
                役職手当 指名2
                <RequireField />
              </FormLabel>
              <Input
                type="number"
                placeholder="役職手当 指名2を入力してください。"
                background="grey.300"
                {...register("shimei2", {
                  valueAsNumber: true,
                  required: "役職手当 指名2が必要です。",
                  min: {
                    value: 0,
                    message: "0円以上で入力してください。",
                  },
                  max: {
                    value: 1_000_000_000,
                    message: "1,000,000,000円以下で入力してください。",
                  },
                })}
                // マウスホイールによる数値編集の無効化
                // see: https://qiita.com/kinakomoti-t/items/ce4402419f2018aae7cf
                onFocus={(e) =>
                  e.target.addEventListener(
                    "wheel",
                    (e) => {
                      e.preventDefault();
                    },
                    { passive: false }
                  )
                }
              />
              <FormErrorMessage>{errors.shimei2?.message}</FormErrorMessage>
            </FormControl>
            <FormControl id="startMonth" mt={3} isInvalid={!!errors.startMonth}>
              <FormLabel>
                適用開始年月
                <RequireField />
              </FormLabel>
              <Controller
                name="startMonth"
                control={control}
                render={({ field }: any) => (
                  <MonthYearPicker
                    startDate={field.value}
                    setStartDate={(date: any) => field.onChange(date)}
                  />
                )}
              />
              <FormErrorMessage>{errors.startMonth?.message}</FormErrorMessage>
            </FormControl>
            <FormControl id="endMonth" mt={3} isInvalid={!!errors.endMonth}>
              <FormLabel>適用終了年月（この月を含む）</FormLabel>
              <Controller
                name="endMonth"
                control={control}
                render={({ field }: any) => (
                  <MonthYearPicker
                    startDate={field.value}
                    setStartDate={(date: any) => field.onChange(date)}
                  />
                )}
              />
              <FormErrorMessage>{errors.endMonth?.message}</FormErrorMessage>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} colorScheme="blue" isLoading={isSubmitting} type="submit">
              OK
            </Button>
            <Button onClick={onClose}>Cancel</Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};
